// betreffAusfuellen.ts — Logik hinter dem „Ausfüllen"-Knopf an der Betreffzeile
// (PO 27.07.2026).
//
// Der Knopf öffnet eine Auswahl aus Vertrag und/oder Risikoobjekt des Kontakts;
// daraus baut `mailSubjectSchema.buildMailSubject` den Betreff:
//
//     <Kontakt> - VSNR <Vertragsnummer> / <Risikoobjekt> / <Betreff>
//
// Hier steht, was aus einer Auswahl wird — bewusst DOM- und React-frei, damit
// die Regeln prüfbar sind, ohne das Fenster zu rendern.

import { risikoobjektLabel, type RiskObjectLite } from "../contactRisks/risikoobjektLabel";
import { trenneAntwortPraefix } from "../../utils/betreffPraefix";

/** Vertrag, soweit die Betreff-Auswahl ihn braucht (Teilmenge von /contracts/by-contact). */
export interface BetreffVertrag {
    id: number;
    contractNbr?: string | null;
    branch?: string | null;
    riskObjects?: BetreffRisiko[] | null;
}

/** Risikoobjekt, soweit die Betreff-Auswahl es braucht. */
export interface BetreffRisiko extends RiskObjectLite {
    id: number;
    riskObjectIdentifier?: string | null;
}

/**
 * Bezeichnung des Risikoobjekts für den Betreff.
 *
 * Nutzt dieselbe Ableitung wie die Risiken-Listen (`risikoobjektLabel`): bei
 * Immobilien ist das die ANSCHRIFT, bei Kfz das Kennzeichen. Genau das ist der
 * „Risikoort", der im Betreff stehen soll — nicht eine interne Bezeichnung.
 */
export function risikoBetreffLabel(r: BetreffRisiko): string {
    const abgeleitet = risikoobjektLabel(r).trim();

    // `risikoobjektLabel` fällt zuletzt auf die TYPBEZEICHNUNG zurück („KFZ",
    // „Immobilie", sonst „Risiko"). Als Betreff-Segment wäre das nichtssagend —
    // „Acme GmbH - VSNR 4711 / Risiko / Schaden" sagt dem Kunden nichts.
    const nurTypbezeichnung =
        !(r.name ?? "").trim() && abgeleitet === ((r.riskType ?? "").trim() || "Risiko");
    if (abgeleitet && !nurTypbezeichnung) return abgeleitet;

    const kennung = (r.riskObjectIdentifier ?? "").trim();
    if (kennung) return kennung;

    // Ohne jede Angabe wenigstens unterscheidbar: „Risiko 9" statt „Risiko".
    return `${abgeleitet || "Risiko"} ${r.id}`;
}

/** Anzeige eines Vertrags in der Auswahlliste. */
export function vertragListenLabel(v: BetreffVertrag): string {
    const nbr = (v.contractNbr ?? "").trim();
    const branch = (v.branch ?? "").trim();
    if (nbr && branch) return `${nbr} · ${branch}`;
    return nbr || branch || `Vertrag ${v.id}`;
}

/**
 * Das Risikoobjekt, das mit einem Vertrag automatisch feststeht.
 *
 * Hängt GENAU EINES am Vertrag, ist die Sache eindeutig — „durch Vertragsauswahl
 * hat man ja ein Risiko". Hängen mehrere daran, wird NICHT geraten: dann muss
 * der Anwender wählen, sonst stünde im Betreff an den Kunden das falsche Objekt.
 */
export function risikoAusVertrag(v: BetreffVertrag | null): BetreffRisiko | null {
    const ros = v?.riskObjects ?? [];
    return ros.length === 1 ? ros[0] : null;
}

/**
 * Der freie Betreff-Teil eines Betreffs, der schon nach Schema gebaut ist.
 *
 * Damit ein zweiter Klick auf „Ausfüllen" nicht „Acme - VSNR 1 / Acme - VSNR 1 /
 * Schaden" erzeugt. Die Erkennung ist bewusst konservativ — sie greift nur bei
 * deutlichen Schema-Merkmalen; im Zweifel bleibt der Text unangetastet und
 * wandert vollständig in den freien Teil.
 *
 * Ein Fehlgriff bleibt folgenlos: das Ergebnis steht als Vorschau im Fenster,
 * bevor es den Betreff ersetzt.
 */
export function freierBetreffTeil(subject: string | null | undefined): string {
    const s = (subject ?? "").trim();
    if (!s) return "";

    // Schema-Merkmale: die VSNR-Marke oder ein „ - "-Kopf zusammen mit „ / ".
    const hatSchema = s.includes(" - VSNR ") || (s.includes(" - ") && s.includes(" / "));
    if (!hatSchema) return s;

    // Der Prefill endet auf „ / " — nach dem Trimmen bleibt ein „/" am Ende, und
    // dahinter steht per Definition noch nichts.
    if (s.endsWith("/")) return "";

    const trenner = s.lastIndexOf(" / ");
    if (trenner >= 0) return s.slice(trenner + 3).trim();

    // Kein „ / " → alles nach dem „ - " ist entweder VSNR-Marke (dann gibt es
    // keinen freien Teil) oder eben der freie Teil.
    const kopf = s.indexOf(" - ");
    const rest = kopf >= 0 ? s.slice(kopf + 3).trim() : s;
    return /^VSNR\b/.test(rest) ? "" : rest;
}

/**
 * Reply-/Forward-Marken bleiben vorn stehen.
 *
 * Antwortet jemand aus dem Composer heraus und füllt dann den Betreff aus, darf
 * das „AW:" nicht verloren gehen — sonst reißt der Thread in Outlook.
 *
 * Die Regel selbst liegt seit Cluster VORGANG (E6) in `utils/betreffPraefix.ts`
 * — der Vorgang-Erstell-Flow braucht sie ebenfalls und soll dafür nicht in
 * `components/mail/**` hineingreifen. Hier bleibt nur der Re-Export, damit
 * Composer und BetreffPicker unverändert weiterlaufen.
 */
export { trenneAntwortPraefix };

/**
 * Führende Kunden-Klammer am Betreff-Anfang: „(Acme GmbH / …".
 *
 * Ein Muster: „(Acme GmbH / Sturmschaden Halle 2". Wandert so ein Betreff in
 * einen Vorgang, steht der Kunde doppelt da — der Vorgang hängt ohnehin an ihm.
 * Der Anfang bis zum ERSTEN Schrägstrich fällt deshalb weg (PO, Runde 3:
 * „beim Befüllen dann (Kundenname /) auch mit rausnehmen").
 *
 * Bewusst eng gefasst, damit die Regel nichts Fachliches abschneidet:
 *  - Sie greift nur ganz vorn — eine Klammer MITTEN im Betreff
 *    („Schaden (Halle 2 / Dach) melden") bleibt unberührt.
 *  - Sie verlangt die RUNDE Klammer. Das Courté-Schema selbst bleibt damit
 *    vollständig stehen (Entscheidung E04): „Acme GmbH - VSNR 4711 / Halle 2"
 *    hat vorn keine Klammer, und die Vorgangsnummer steht in ECKIGEN Klammern.
 *  - Im Kundennamen darf keine weitere Klammer stehen („(Acme (Nord) / …"
 *    bleibt unangetastet) — im Zweifel lieber nichts abschneiden.
 *  - Der Teil vor dem Schrägstrich muss mindestens EINEN Buchstaben tragen
 *    (Runde 3). Ohne diese Bedingung fraß die Regel Zähler und Datumsangaben:
 *    „(2/3) Nachtrag" wurde zu „3 Nachtrag", „(01/2026) Abrechnung" zu
 *    „2026 Abrechnung". Ein Kundenname ohne Buchstaben ist keiner.
 *
 * Eine Antwort-/Weiterleitungsmarke davor stört nicht: sie wird abgetrennt,
 * bleibt aber am Ergebnis („AW: (Acme GmbH / Sturm" → „AW: Sturm"). Das
 * Entfernen der Marke ist eine ANDERE Regel (`betreffOhneAntwortPraefix`) und
 * gehört dem Aufrufer.
 */
const KUNDEN_KLAMMER_RE = /^\(\s*([^()/]*[^()/\s])\s*\/\s*/;

/** Trägt der Teil vor dem Schrägstrich überhaupt einen Namen (Buchstaben)? */
function istNamensteil(text: string): boolean {
    return /\p{L}/u.test(text);
}

/**
 * Das „)" einer Klammer, deren „(" gerade entfernt wurde.
 *
 * „(Acme GmbH / VSNR 4711) Sturm" verliert vorn „(Acme GmbH / " — ohne diesen
 * Schritt bliebe „VSNR 4711) Sturm" mit einer Klammer stehen, die nichts mehr
 * öffnet. Eine EIGENE, vollständige Klammer im Rest bleibt selbstverständlich.
 */
function ohneVerwaisteKlammerZu(text: string): string {
    const zu = text.indexOf(")");
    if (zu < 0) return text;
    const auf = text.indexOf("(");
    if (auf >= 0 && auf < zu) return text;
    return `${text.slice(0, zu).trimEnd()} ${text.slice(zu + 1).trimStart()}`.trim();
}

/**
 * Betreff ohne die führende Kunden-Klammer (siehe `KUNDEN_KLAMMER_RE`).
 *
 * Ohne Treffer kommt der Betreff unverändert zurück. Bleibt nach dem Entfernen
 * nichts übrig („(Acme GmbH / "), ist das Ergebnis leer — wie bei
 * `betreffOhneAntwortPraefix` entscheidet dann der Aufrufer, was er anbietet.
 */
export function betreffOhneKundenKlammer(subject: string | null | undefined): string {
    const roh = (subject ?? "").trim();
    const { praefix, rest } = trenneAntwortPraefix(roh);
    const treffer = KUNDEN_KLAMMER_RE.exec(rest);
    if (!treffer || !istNamensteil(treffer[1] ?? "")) return roh;
    const uebrig = ohneVerwaisteKlammerZu(rest.slice(treffer[0].length).trim());
    return uebrig ? `${praefix}${uebrig}` : "";
}
